import { useState } from 'react'
import { TraceRegion } from '../lib/trace'
import { api, unlock } from '../api'
import { theme } from '../theme'
import { Button, Card, ChoiceRow, Field, Heading, Page, inputStyle } from '../components/ui'
import { LanguageCard } from '../components/LanguageCard'
import { fromServer, screenLanguage, t } from '../lib/i18n'

const OFFSETS = [-3, -2, -1, 0, 1, 2, 3] as const

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

// Newest year first, because a child being set up is far more likely to have
// been born recently than twenty years ago.
const THIS_YEAR = new Date().getFullYear()
const YEARS = Array.from({ length: 26 }, (_, i) => THIS_YEAR - i)
const LANGUAGES = [
  { code: 'zh-TW', label: '中文 (繁體)' },
  { code: 'zh-CN', label: '中文 (简体)' },
  { code: 'es', label: 'Español' },
  { code: 'ko', label: '한국어' },
  { code: 'ja', label: '日本語' },
  { code: 'none', label: 'English only' },
]

/**
 * Gate 3. No child appears on Home until this is filled in.
 *
 * The offset runs -3 to +3, decided 18 Sep 2026. Going below the school year
 * matters as much as going above it: an app that only offers "ahead" quietly
 * tells half of its families it was not built for them.
 */
export function ChildSetup({
  index, onSaved, onFinish,
}: { index: number; onSaved: () => void; onFinish: () => void }) {
  // Held here only so choosing a language on the card redraws this whole
  // form in it, as Settings does (Codex review, 27 Oct 2026).
  const [, setScreens] = useState(screenLanguage())
  const [name, setName] = useState('')
  const [birthMonth, setBirthMonth] = useState('')
  const [birthYear, setBirthYear] = useState('')
  const [year, setYear] = useState(3)
  const [offset, setOffset] = useState<number>(0)
  const [language, setLanguage] = useState('zh-TW')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  // Names saved so far this visit, so pressing Save is visibly doing something.
  const [saved, setSaved] = useState<string[]>([])
  // Each new child's PIN, shown here because it is readable only once.
  const [pins, setPins] = useState<{ name: string; pin: string }[]>([])
  // Set when "finish" was pressed but PINs are still on screen to be copied.
  const [finishing, setFinishing] = useState(false)

  // Both or neither. Half a date is not a date.
  const birth = birthMonth && birthYear ? `${birthYear}-${birthMonth}` : null
  const inFuture = Boolean(
    birth && (Number(birthYear) > THIS_YEAR ||
      (Number(birthYear) === THIS_YEAR && Number(birthMonth) > new Date().getMonth() + 1)),
  )

  const save = async (then: 'another' | 'finish') => {
    if (!name.trim()) { setProblem(t('A first name is enough.')); return }
    if (birthMonth && !birthYear) { setProblem(t('Pick a year as well.')); return }
    if (birthYear && !birthMonth) { setProblem(t('Pick a month as well.')); return }
    if (inFuture) { setProblem(t('That month has not happened yet.')); return }
    setBusy(true); setProblem(null)
    try {
      const made = await api.addChild({
        display_name: name.trim(),
        school_year: year,
        year_offset: offset,
        birth_month: birth,
        first_language: language,
      } as never)
      // The new child's ticket, so setting their picture works now the
      // family may need PINs (D-065).
      if (made.ticket) unlock.setChild(made.child.learner_id, made.ticket)
      const newPins = made.pin ? [...pins, { name: name.trim(), pin: made.pin }] : pins
      setPins(newPins)
      if (then === 'another') {
        setSaved((s) => [...s, name.trim()])
        setName(''); setBirthMonth(''); setBirthYear(''); setOffset(0)
        onSaved()
      } else if (index >= 2 && newPins.length) {
        // The family now has two or more children, so PINs are in use. Hold
        // the screen so the grown-up can copy them down; they cannot be shown
        // again. `index` is this child's place in the family, so this also
        // catches a second child added months after the first.
        setFinishing(true)
      } else {
        onFinish()
      }
    } catch (err) {
      setProblem(fromServer(err, 'That did not save.'))
    } finally {
      setBusy(false)
    }
  }

  const effective = year + offset
  const offsetLine =
    offset === 0 ? t('Work pitched at school year {year}.', { year })
    : offset > 0 ? t('Work pitched at year {year} — {n} ahead.', { year: effective, n: offset })
    : t('Work pitched at year {year} — {n} behind.', { year: effective, n: Math.abs(offset) })

  return (
    <Page width={560}>
      <TraceRegion id="T2" writes note="each answer here is saved to a column of learner: display_name, school_year, year_offset, birth_date, first_language. A PIN is made too, in T25 learner_pin">
      <div style={{ fontSize: 13, color: theme.faint, marginBottom: 6 }}>
        {t('Child {n}', { n: index })}
      </div>
      <Heading sub={t('This decides how hard the work starts out. You can change it any time.')}>
        {t('Set up a child')}
      </Heading>

      {/* Asked once, with the first child: the family's home language, and
          the grown-up screens in it (Sam, 25 Oct 2026). Also in Settings. */}
      {index === 1 ? <div style={{ marginBottom: 14 }}><LanguageCard onChanged={() => setScreens(screenLanguage())} /></div> : null}

      <Card>
        <Field label={t('First name')}>
          <input
            style={{ ...inputStyle }}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Charles"
            autoFocus
          />
        </Field>

        {/* Month and year only. The exact day tells the app nothing it needs
            and is precisely the kind of detail a parent should not have to
            hand over about a child. */}
        <Field
          label={t('Born')}
          hint={t('Optional, and month and year only. We never ask for the exact date.')}
        >
          {/* Two pickers rather than a date field. A wheel on a phone, a
              dropdown on a desktop, and no way to type 2027 by accident. */}
          <div style={{ display: 'flex', gap: 8 }}>
            <select
              style={{ ...inputStyle, flex: 2, cursor: 'pointer' }}
              value={birthMonth}
              onChange={(e) => setBirthMonth(e.target.value)}
            >
              <option value="">{t('Month')}</option>
              {MONTHS.map((label, i) => (
                <option key={label} value={String(i + 1).padStart(2, '0')}>{t(label)}</option>
              ))}
            </select>
            <select
              style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
            >
              <option value="">{t('Year')}</option>
              {YEARS.map((y) => <option key={y} value={String(y)}>{y}</option>)}
            </select>
          </div>
          {inFuture ? (
            <div style={{ marginTop: 8, fontSize: 15, color: theme.red }}>
              {t('That month has not happened yet.')}
            </div>
          ) : null}
        </Field>

        <Field label={t('School year')}>
          <ChoiceRow
            options={[0, 1, 2, 3, 4, 5, 6, 7, 8] as const}
            value={year}
            onChange={setYear}
            labels={(y) => (y === 0 ? 'K' : String(y))}
          />
        </Field>

        <Field
          label={t('How far ahead or behind should the work sit?')}
          hint={t('Most children start at their school year. Move it if the work turns out too easy or too hard.')}
        >
          <ChoiceRow
            options={OFFSETS}
            value={offset}
            onChange={setOffset}
            labels={(o) => (o === 0 ? t('At year') : o > 0 ? `+${o}` : `−${Math.abs(o)}`)}
          />
          <div style={{
            marginTop: 10, padding: '10px 12px', background: theme.orangePale,
            borderRadius: 8, fontSize: 15, color: theme.orangeDark, fontWeight: 600,
          }}>
            {offsetLine}
          </div>
        </Field>

        <Field
          label={t('First language')}
          hint={t('Used for the translation button, and spoken in that language.')}

        >
          <select
            style={{ ...inputStyle, cursor: 'pointer' }}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{t(l.label)}</option>
            ))}
          </select>
        </Field>

        {finishing ? (
          <div role="status" style={{
            margin: '0 0 14px', padding: 14, background: theme.blueFaint,
            border: `1px solid ${theme.bluePale}`, borderRadius: 10,
          }}>
            <strong style={{ display: 'block', marginBottom: 6, color: theme.text }}>
              {t('Each child has a PIN')}
            </strong>
            <p style={{ margin: '0 0 10px', fontSize: 14, color: theme.text2, lineHeight: 1.5 }}>
              {t("They type it before their practice opens, so brothers and sisters don't answer each other's questions. Write these down now — they can't be shown again. Any child not listed here can be given a PIN in Settings, and you can set new ones there at any time.")}
            </p>
            {pins.map((p) => (
              <div key={p.name} style={{ fontSize: 18, margin: '2px 0' }}>
                {p.name}: <strong style={{ fontFamily: "'Fredoka', sans-serif", letterSpacing: '0.18em' }}>{p.pin}</strong>
              </div>
            ))}
            <div style={{ marginTop: 12 }}>
              <Button onClick={onFinish}>{t("I've written them down")}</Button>
            </div>
          </div>
        ) : null}

        {saved.length ? (
          <div style={{
            margin: '0 0 14px', padding: '10px 12px', background: theme.greenPale,
            borderRadius: 8, fontSize: 15, color: theme.greenDarker, lineHeight: 1.5,
          }}>
            {t('Saved so far:')} <strong>{saved.join(', ')}</strong>{t('. Fill this in for the next child, or press Save and finish.')}
          </div>
        ) : null}

        {problem ? (
          <p style={{ color: theme.red, fontSize: 15, margin: '0 0 14px' }}>{problem}</p>
        ) : null}

        {/* Two ways out. One child is a perfectly normal family. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Button full disabled={busy} onClick={() => save('another')}>
            {t('Save and add another child')}
          </Button>
          <Button full tone="quiet" disabled={busy} onClick={() => save('finish')}>
            {t('Save and finish')}
          </Button>
        </div>
      </Card>
      </TraceRegion>
    </Page>
  )
}
